import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { createOrder, verifyPayment, loadRazorpayScript } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", address: "", city: "", pincode: "" };

export const CheckoutDialog = () => {
  const { checkoutOpen, setCheckoutOpen, item, plan, total, config, clearCart } = useCart();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const valid =
    form.name.trim() &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.phone.trim().length >= 10 &&
    form.address.trim();

  const finish = () => {
    clearCart();
    setForm(EMPTY);
    setCheckoutOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid || !plan) {
      toast.error("Please fill all required details correctly.");
      return;
    }
    setLoading(true);
    try {
      const order = await createOrder({
        plan_id: plan.id,
        quantity: item.qty,
        customer: form,
      });

      if (config.payments_enabled && order.razorpay_order_id) {
        const ok = await loadRazorpayScript();
        if (!ok) throw new Error("Failed to load payment gateway");
        const rzp = new window.Razorpay({
          key: config.razorpay_key_id,
          amount: order.amount * 100,
          currency: "INR",
          name: "Veda Vitals",
          description: `${order.plan_title} · ACV Effervescent Tablets`,
          order_id: order.razorpay_order_id,
          prefill: { name: form.name, email: form.email, contact: form.phone },
          theme: { color: "#D4AF37" },
          handler: async (resp) => {
            try {
              await verifyPayment({
                order_id: order.id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success("Payment successful! Your order is confirmed.");
              finish();
            } catch (err) {
              toast.error("Payment verification failed. Please contact support.");
            }
          },
        });
        rzp.open();
      } else {
        toast.success(
          "Order placed! Our team will reach out shortly to confirm and set up your consultation."
        );
        finish();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
      <DialogContent
        data-testid="checkout-dialog"
        className="bg-obsidian border border-white/10 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white text-left">
            Checkout
          </DialogTitle>
          <DialogDescription className="text-vmuted text-left">
            Enter your details to complete your Veda Vitals order.
          </DialogDescription>
        </DialogHeader>

        {plan && (
          <div className="glass rounded-xl p-4 flex items-center justify-between mb-2">
            <div>
              <p className="font-serif text-lg text-white">
                {plan.title} × {item.qty}
              </p>
              <p className="text-xs text-vmuted">Free consultation, diet plan & coach included</p>
            </div>
            <p className="font-serif text-2xl text-gold">₹{total}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="ck-name" className="text-vmuted">Full name *</Label>
            <Input id="ck-name" data-testid="ck-name" value={form.name} onChange={set("name")}
              className="bg-white/5 border-white/10 focus-visible:ring-gold" placeholder="Your name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="ck-email" className="text-vmuted">Email *</Label>
              <Input id="ck-email" data-testid="ck-email" type="email" value={form.email} onChange={set("email")}
                className="bg-white/5 border-white/10 focus-visible:ring-gold" placeholder="you@email.com" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ck-phone" className="text-vmuted">Phone *</Label>
              <Input id="ck-phone" data-testid="ck-phone" value={form.phone} onChange={set("phone")}
                className="bg-white/5 border-white/10 focus-visible:ring-gold" placeholder="10-digit number" />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="ck-address" className="text-vmuted">Delivery address *</Label>
            <Input id="ck-address" data-testid="ck-address" value={form.address} onChange={set("address")}
              className="bg-white/5 border-white/10 focus-visible:ring-gold" placeholder="House, street, area" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="ck-city" className="text-vmuted">City</Label>
              <Input id="ck-city" data-testid="ck-city" value={form.city} onChange={set("city")}
                className="bg-white/5 border-white/10 focus-visible:ring-gold" placeholder="City" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ck-pincode" className="text-vmuted">Pincode</Label>
              <Input id="ck-pincode" data-testid="ck-pincode" value={form.pincode} onChange={set("pincode")}
                className="bg-white/5 border-white/10 focus-visible:ring-gold" placeholder="496225" />
            </div>
          </div>

          <button
            type="submit"
            data-testid="ck-submit-btn"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-obsidian font-medium hover:bg-gold-light transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {config.payments_enabled ? `Pay ₹${total}` : `Place Order · ₹${total}`}
              </>
            )}
          </button>
          <p className="flex items-center justify-center gap-2 text-xs text-vmuted">
            <ShieldCheck className="h-3.5 w-3.5 text-gold" /> Secure checkout · Made in India
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
